import { DateRange } from '@angular/material/datepicker';

import { FranchiseLocation } from '../franchise.model';
import { ReservationServiceItem } from './reservation-service.model';
import { ReservationPetInfo } from '../reservation-pet-info.model';

import * as lodash from 'lodash';
import { CategoriesWithRules } from './reservation-types';

export interface IReservationRequestItem {
  selectedLocation?: FranchiseLocation;
  selectedPets?: ReservationPetInfo[];
  coreServices?: ReservationServiceItem[];
  coreCategoryIds?: string[];
  selectedDateRange?: DateRange<any>;
  addonServices?: { [petId: string]: ReservationServiceItem[] };
  addonCategoryIds?: { [petId: string]: string[] };
  reservationNotes?: string;
}

export class ReservationRequestItem implements IReservationRequestItem {
  // #region -> Property <-
  // ----------

  /*
    Location
  */
  selectedLocation: FranchiseLocation;

  /*
    Pets
  */
  selectedPets: ReservationPetInfo[] = [];

  /*
    Services
  */
  coreServices: ReservationServiceItem[] = [];
  coreCategoryIds: string[] = [];

  /*
    Date(s)
  */
  selectedDateRange: DateRange<any>;

  /*
    Add-ons
  */
  addonServices: { [petId: string]: ReservationServiceItem[] } = {};
  addonCategoryIds: { [petId: string]: string[] } = {};
  reservationNotes: string;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(newItem: IReservationRequestItem) {
    //
    this.selectedLocation = newItem.selectedLocation || undefined;
    this.selectedPets = newItem.selectedPets || [];
    this.coreServices = newItem.coreServices || [];
    this.coreCategoryIds = newItem.coreCategoryIds || [];
    this.selectedDateRange = newItem.selectedDateRange || undefined;
    this.addonServices = newItem.addonServices || {};
    this.addonCategoryIds = newItem.addonCategoryIds || {};
    this.reservationNotes =
      newItem.reservationNotes !== undefined
        ? newItem.reservationNotes
        : undefined;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Immutable <-
  // ----------

  json(): IReservationRequestItem {
    //
    return lodash.cloneDeep({
      selectedLocation: this.selectedLocation,
      selectedPets: this.selectedPets,
      coreServices: this.coreServices,
      coreCategoryIds: this.coreCategoryIds,
      selectedDateRange: this.selectedDateRange,
      addonServices: this.addonServices,
      addonCategoryIds: this.addonCategoryIds,
      reservationNotes: this.reservationNotes,
    });
  }

  clone(): ReservationRequestItem {
    //
    return new ReservationRequestItem(this.json());
  }

  update(
    updatedData: Partial<IReservationRequestItem>
  ): ReservationRequestItem {
    //
    const newData: IReservationRequestItem = { ...this.json(), ...updatedData };

    // Return a new reference
    return new ReservationRequestItem(newData);
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Get <-
  // ----------

  includeCoreCategory(categoryId: string): boolean {
    //
    return this.coreCategoryIds.includes(categoryId) ? true : false;
  }

  includeCoreService(serviceId: string): boolean {
    //
    if (
      lodash.find(
        this.coreServices,
        (service) => service.serviceId === serviceId
      )
    ) {
      // Found an match
      return true;
    }

    //
    return false;
  }

  includeAddonCategory(petId: string, categoryId: string): boolean {
    //
    for (const id in this.addonCategoryIds) {
      //
      if (id === petId) {
        return this.addonCategoryIds[petId].includes(categoryId) ? true : false;
      }
    }

    //
    return false;
  }

  includeAddonService(petId: string, serviceId: string): boolean {
    //
    for (const id in this.addonServices) {
      //
      if (
        id === petId &&
        lodash.find(
          this.addonServices[petId],
          (service) => service.serviceId === serviceId
        )
      ) {
        return true;
      }
    }

    //
    return false;
  }

  getAddonQuantity(petId: string, serviceId: string): number {
    //
    let retPrice = 1;

    if (this.addonServices.hasOwnProperty(petId)) {
      //
      this.addonServices[petId].forEach((service) => {
        if (service.serviceId === serviceId) {
          //
          retPrice = service.serviceQuantity;
        }
      });
    }

    //
    return retPrice;
  }

  hasCoreServices(): boolean {
    //
    return this.coreServices.length > 0;
  }

  hasAddonServices(): boolean {
    //
    for (const petId in this.addonServices) {
      if (this.addonServices.hasOwnProperty(petId)) {
        if (this.addonServices[petId].length > 0) {
          return true;
        }
      }
    }

    //
    return false;
  }

  hasAddonServicesForPet(petId: string): boolean {
    //
    if (this.addonServices.hasOwnProperty(petId)) {
      return this.addonServices[petId].length > 0;
    }

    //
    return false;
  }

  canBeSelectable(
    petId: string,
    serviceToAdd: ReservationServiceItem
  ): boolean {
    //
    if (this.addonServices.hasOwnProperty(petId)) {
      const categoryIdList = this.addonCategoryIds[petId];
      return serviceToAdd.canBeSelectable(categoryIdList);
    }

    // Otherwise, all "selectable"
    return true;
  }

  /**
   * Check if the addon services are all valid.
   *
   * It is mainly for checking the "quantity".
   */
  isValidForAddonServices(): boolean {
    //
    for (const petId in this.addonServices) {
      if (this.addonServices.hasOwnProperty(petId)) {
        const serviceList = this.addonServices[petId];
        if (
          lodash.find(
            serviceList,
            (service) =>
              service.needQuantityInput === true &&
              !(
                service.serviceQuantity > 0 &&
                service.serviceQuantity < 100 &&
                Number.isInteger(service.serviceQuantity)
              )
          )
        ) {
          // Found an invalid element
          return false;
        }
      }
    }

    //
    return true;
  }

  calcTotalPriceOfCoreServices(categoryId: string): number {
    //
    let totalPrice = 0;

    //
    for (const service of this.coreServices) {
      if (service.serviceCategoryId === categoryId) {
        totalPrice += service.servicePrice;
      }
    }

    return totalPrice;
  }

  calcTotalPriceOfAllServices(): number {
    //
    let totalPrice = 0;

    for (const pet of this.selectedPets) {
      if (this.coreServices.length > 0) {
        // Core Services
        for (const service of this.coreServices) {
          // Calc the "service quantity" for certain services
          //  - "boarding" service - it is based on the "nights"
          if (
            service.serviceCategoryId === CategoriesWithRules.Boarding &&
            this.selectedDateRange &&
            this.selectedDateRange.end
          ) {
            // calculate the number of days in the range, subtracting one so we count only the nights
            const diffDays = Math.round(
              Math.abs(
                (this.selectedDateRange.start - this.selectedDateRange.end) /
                  (24 * 60 * 60 * 1000)
              )
            );
            service.serviceQuantity = diffDays;
          }

          // Calc and add the price
          totalPrice += service.servicePrice * service.serviceQuantity;
        }

        // Addon Services
        if (this.addonServices.hasOwnProperty(pet.id)) {
          const serviceList = this.addonServices[pet.id];
          // Multiply by quantity
          for (const service of serviceList) {
            totalPrice += service.servicePrice * service.serviceQuantity;
          }
        }
      }
    }

    //
    return totalPrice;
  }

  isNewLocationSelected(
    selected: FranchiseLocation,
    existingLocationList: FranchiseLocation[]
  ): boolean {
    //
    if (!selected) {
      return false;
    }

    if (
      lodash.find(
        existingLocationList,
        (location) => selected.id === location.id
      )
    ) {
      // Found an match
      return false;
    }

    //
    return true;
  }

  isPetSelected(petId: string): boolean {
    //
    if (lodash.find(this.selectedPets, (pet) => pet.id === petId)) {
      // Found an match
      return true;
    }

    //
    return false;
  }

  isPetSelectionChanged(pets: ReservationPetInfo[]): boolean {
    //
    if (
      this.selectedPets.length > 0 &&
      !lodash.isEqual(this.selectedPets, pets)
    ) {
      return true;
    }
  }

  /**
   * Check if the "service" part gets changed.
   *
   * NOTE:
   * - If the current state is "empty", it is "NOT" treated as "changed".
   *
   * @param newReservationRequest The new request to be checked.
   * @returns
   */
  isServiceSelectionChanged(
    newReservationRequest: ReservationRequestItem
  ): boolean {
    //
    if (
      this.hasCoreServices() &&
      !lodash.isEqual(this.coreServices, newReservationRequest.coreServices)
    ) {
      return true;
    }

    return false;
  }

  isDateSelectionChanged(newDateRange: DateRange<any>): boolean {
    //
    return !lodash.isEqual(this.selectedDateRange, newDateRange);
  }

  /**
   * Check if the "addon" part gets changed.
   *
   * NOTE:
   * - If the current state is "empty", it is "NOT" treated as "changed".
   *
   * @param newReservationRequest The new request to be checked.
   * @returns
   */
  isAddonSelectionChanged(
    newReservationRequest: ReservationRequestItem
  ): boolean {
    //
    if (
      this.hasAddonServices() &&
      !lodash.isEqual(this.addonServices, newReservationRequest.addonServices)
    ) {
      return true;
    }

    //
    if (
      this.reservationNotes !== undefined &&
      this.reservationNotes !== newReservationRequest.reservationNotes
    ) {
      return true;
    }

    return false;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Set <-
  // ----------

  addNewCoreService(
    newService: ReservationServiceItem
  ): ReservationRequestItem {
    //
    this.coreServices = this._addNewService(newService, this.coreServices);
    this.coreCategoryIds = this._buildCategoryIdList(this.coreServices);

    //
    return this.clone();
  }

  removeCoreService(
    targetService: ReservationServiceItem
  ): ReservationRequestItem {
    this.coreServices = this._removeService(targetService, this.coreServices);
    this.coreCategoryIds = this._buildCategoryIdList(this.coreServices);

    //
    return this.clone();
  }

  addNewAddonService(
    petId: string,
    newService: ReservationServiceItem
  ): ReservationRequestItem {
    //
    if (!this.addonServices.hasOwnProperty(petId)) {
      this.addonServices[petId] = [];
      this.addonCategoryIds[petId] = [];
    }
    this.addonServices[petId] = this._addNewService(
      newService,
      this.addonServices[petId]
    );
    this.addonCategoryIds[petId] = this._buildCategoryIdList(
      this.addonServices[petId]
    );

    //
    return this.clone();
  }

  removeAddonService(
    petId: string,
    targetService?: ReservationServiceItem
  ): ReservationRequestItem {
    if (this.addonServices.hasOwnProperty(petId)) {
      if (targetService) {
        // Just remove a specific one
        this.addonServices[petId] = this._removeService(
          targetService,
          this.addonServices[petId]
        );
        this.addonCategoryIds[petId] = this._buildCategoryIdList(
          this.addonServices[petId]
        );

        // Check if need to remove this entry for the pet if it is empty
        if (this.addonCategoryIds[petId].length === 0) {
          //
          lodash.unset(this.addonCategoryIds, petId);
          lodash.unset(this.addonServices, petId);
        }
      } else {
        // Remove all
        lodash.unset(this.addonCategoryIds, petId);
        lodash.unset(this.addonServices, petId);
      }
    }

    //
    return this.clone();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Set <-
  // ----------

  addQuantity(
    petId: string,
    serviceId: string,
    quantity: number
  ): ReservationRequestItem {
    //
    if (this.addonServices.hasOwnProperty(petId)) {
      const serviceList = this.addonServices[petId];
      serviceList.forEach((service) => {
        if (service.serviceId === serviceId) {
          service.serviceQuantity = quantity;
        }
      });
    }

    //
    return this.clone();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _addNewService(
    newService: ReservationServiceItem,
    targetList: ReservationServiceItem[]
  ): ReservationServiceItem[] {
    //
    const newList = lodash.cloneDeep(targetList);
    if (!lodash.find(targetList, ['serviceId', newService.serviceId])) {
      // Not Found
      newList.push(lodash.cloneDeep(newService));
    }

    return newList;
  }

  private _removeService(
    targetService: ReservationServiceItem,
    targetList: ReservationServiceItem[]
  ): ReservationServiceItem[] {
    //
    const newList = lodash.cloneDeep(targetList);
    if (lodash.find(targetList, ['serviceId', targetService.serviceId])) {
      // Found
      lodash.remove(newList, {
        serviceId: targetService.serviceId,
      });
    }

    return newList;
  }

  private _buildCategoryIdList(
    serviceList: ReservationServiceItem[]
  ): string[] {
    // Need to remove the duplicates
    if (serviceList.length === 0) {
      return [];
    }
    return lodash.sortedUniq(lodash.map(serviceList, 'serviceCategoryId'));
  }

  // ----------
  // #endregion

  /////////////////////////////
}
